import { useState, useEffect } from "react";
import { AlertTriangle, Clock, TrendingUp, Filter, RefreshCw, ChevronDown, ChevronUp, Brain, Loader2, MessageSquare } from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


function formatJours(jours) {
  if (jours === null || jours === undefined) return "N/A";
  if (jours < 7) return jours + "j";
  if (jours < 30) return Math.floor(jours / 7) + " sem.";
  if (jours < 365) return Math.floor(jours / 30) + " mois";
  return Math.floor(jours / 365) + " an(s)";
}

function getUrgenceColor(retard) {
  if (!retard) return "bg-slate-500";
  if (retard > 60) return "bg-red-500";
  if (retard > 30) return "bg-orange-500";
  if (retard > 14) return "bg-yellow-500";
  return "bg-blue-500";
}

function getStatutColor(statut) {
  if (!statut) return "bg-slate-500";
  if (statut.includes("Terminé")) return "bg-green-500";
  if (statut.includes("Chantier")) return "bg-amber-500";
  if (statut.includes("Octroi") || statut.includes("Négo")) return "bg-purple-500";
  if (statut.includes("Dépôt") || statut.includes("R2")) return "bg-indigo-500";
  if (statut.includes("AMO")) return "bg-blue-500";
  if (statut.includes("Qualité")) return "bg-teal-500";
  return "bg-cyan-500";
}

function DossierCard({ dossier }) {
  const [expanded, setExpanded] = useState(false);
  const [analyseIA, setAnalyseIA] = useState(null);
  const [loadingAnalyse, setLoadingAnalyse] = useState(false);
  const [parcours, setParcours] = useState(null);
  const [loadingParcours, setLoadingParcours] = useState(false);

  const loadAnalyseIA = async () => {
    if (analyseIA) return;
    setLoadingAnalyse(true);
    try {
      const response = await authFetch(API + "/api/analyse/diagnostic-blocage/" + dossier.rowNumber);
      if (response.ok) {
        const data = await response.json();
        setAnalyseIA(data);
      }
    } catch (e) {
      console.error("Erreur analyse IA:", e);
    }
    setLoadingAnalyse(false);
  };

  const loadParcours = async () => {
    if (parcours) return;
    setLoadingParcours(true);
    try {
      const response = await authFetch(API + "/api/parcours/client/" + dossier.rowNumber);
      if (response.ok) {
        const data = await response.json();
        setParcours(data);
      }
    } catch (e) {
      console.error("Erreur parcours:", e);
    }
    setLoadingParcours(false);
  };

  const handleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded) {
      loadAnalyseIA();
    }
  };
  
  return (
    <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden hover:border-red-500/30 transition-colors">
      <div className="p-4 cursor-pointer" onClick={handleExpand}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={"w-2 h-2 rounded-full " + getUrgenceColor(dossier.retardJours)} />
              <h4 className="text-white font-medium truncate">{dossier.nom} {dossier.prenom}</h4>
              <span className="text-xs text-slate-500">({dossier.dpt})</span>
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className={"text-xs px-2 py-1 rounded-full text-white " + getStatutColor(dossier.statut)}>{dossier.statut}</span>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Depuis {formatJours(dossier.joursStatut)}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={"text-sm font-bold " + (dossier.retardJours > 30 ? "text-red-400" : "text-orange-400")}>+{dossier.retardJours}j</span>
            <span className="text-xs text-slate-500">de retard</span>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400 mt-1" /> : <ChevronDown className="w-4 h-4 text-slate-400 mt-1" />}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-700/50 pt-3 space-y-4">
          <p className="text-sm text-orange-300">⚠️ {dossier.raisonBlocage}</p>
          
          <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg border border-purple-500/30 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-5 h-5 text-purple-400" />
              <h5 className="text-purple-200 font-semibold">🤖 Analyse IA du blocage</h5>
            </div>
            
            {loadingAnalyse ? (
              <div className="flex items-center gap-2 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Analyse en cours...</span>
              </div>
            ) : analyseIA ? (
              <div className="space-y-3">
                <p className="text-white text-sm whitespace-pre-wrap">{analyseIA.diagnostic}</p>
                
                {analyseIA.resumesAppels && analyseIA.resumesAppels.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-purple-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-blue-400" />
                      <span className="text-blue-300 text-xs font-medium">Basé sur {analyseIA.resumesAppels.length} échange(s)</span>
                    </div>
                  </div>
                )}
                
                {analyseIA.recommandations && (
                  <div className="mt-3 pt-3 border-t border-purple-500/20">
                    <span className="text-purple-300 text-xs font-medium">💡 Actions recommandées:</span>
                    <p className="text-slate-300 text-sm mt-1">{analyseIA.recommandations}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Cliquez pour générer l'analyse</p>
            )}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); loadParcours(); }}
            disabled={loadingParcours}
            className="w-full py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 rounded-lg text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {loadingParcours ? <><Loader2 className="w-4 h-4 animate-spin" /> Chargement...</> : parcours ? "Parcours chargé ✓" : "Voir le parcours complet"}
          </button>

          {parcours && parcours.client && (
            <div className="bg-slate-900/50 rounded-lg p-4">
              <h5 className="text-white font-semibold mb-3">📋 Parcours</h5>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {parcours.client.dateR1 && <div><span className="text-slate-400">R1:</span> <span className="text-white">{parcours.client.dateR1}</span></div>}
                {parcours.client.dateTransfertAmo && <div><span className="text-slate-400">Transfert AMO:</span> <span className="text-white">{parcours.client.dateTransfertAmo}</span></div>}
                {parcours.client.vtAmo && <div><span className="text-slate-400">VT AMO:</span> <span className="text-white">{parcours.client.vtAmo}</span></div>}
                {parcours.client.dateR2 && <div><span className="text-slate-400">R2:</span> <span className="text-white">{parcours.client.dateR2}</span></div>}
                {parcours.client.dateDepotAnah && <div><span className="text-slate-400">Dépôt ANAH:</span> <span className="text-white">{parcours.client.dateDepotAnah}</span></div>}
                {parcours.client.dateOctroi && <div><span className="text-slate-400">Octroi:</span> <span className="text-white">{parcours.client.dateOctroi}</span></div>}
                {parcours.client.accordChantier && <div><span className="text-slate-400">Accord:</span> <span className="text-white">{parcours.client.accordChantier}</span></div>}
                {parcours.client.dateDebutChantier && <div><span className="text-slate-400">Début:</span> <span className="text-white">{parcours.client.dateDebutChantier}</span></div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AnalyseDossiersBloques() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtreStatut, setFiltreStatut] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(API + "/api/analyse/dossiers-bloques");
      if (!response.ok) throw new Error("Erreur chargement");
      const result = await response.json();
      setData(result);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const filteredDossiers = (data && data.dossiersBloques) 
    ? data.dossiersBloques.filter(d => !filtreStatut || d.statut === filtreStatut)
    : [];

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-8">
        <div className="flex items-center justify-center gap-3">
          <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
          <span className="text-slate-400">Analyse en cours...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-red-500/50 p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-400">{error}</p>
        <button onClick={loadData} className="mt-4 px-4 py-2 bg-red-600 rounded-lg text-white">Réessayer</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-xl border border-red-700/30 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-red-500 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-red-100">🚨 Dossiers Bloqués</h2>
              <p className="text-red-200/60">{data ? data.totalBloques : 0} dossiers en retard sur {data ? data.totalDossiers : 0} actifs</p>
            </div>
          </div>
          <button onClick={loadData} className="p-3 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-colors">
            <RefreshCw className="w-5 h-5 text-slate-300" />
          </button>
        </div>
      </div>

      {data && data.statsParEtape && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-400" />
            Répartition des blocages
          </h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFiltreStatut("")} className={"px-3 py-2 rounded-lg text-sm transition-colors " + (!filtreStatut ? "bg-red-500 text-white" : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50")}>
              Tous ({data.totalBloques})
            </button>
            {Object.entries(data.statsParEtape).map(function([statut, info]) {
              return (
                <button key={statut} onClick={() => setFiltreStatut(filtreStatut === statut ? "" : statut)} className={"px-3 py-2 rounded-lg text-sm transition-colors " + (filtreStatut === statut ? "bg-red-500 text-white" : "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50")}>
                  {statut} <span className="ml-2 px-1.5 py-0.5 bg-black/30 rounded text-xs">{info.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {data && data.moyennesEtapes && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Durées moyennes entre étapes (référence)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
            <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 rounded-lg p-3 border border-cyan-700/30">
              <div className="text-cyan-300 text-xs font-medium">Prise RDV → R1</div>
              <div className="text-white font-bold text-lg">{data.moyennesEtapes.prise_rdv_to_r1 ? data.moyennesEtapes.prise_rdv_to_r1 + "j" : "N/A"}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-lg p-3 border border-blue-700/30">
              <div className="text-blue-300 text-xs font-medium">R1 → VT AMO</div>
              <div className="text-white font-bold text-lg">{data.moyennesEtapes.r1_to_vt_amo ? data.moyennesEtapes.r1_to_vt_amo + "j" : "N/A"}</div>
            </div>
            <div className="bg-gradient-to-br from-indigo-900/30 to-indigo-800/20 rounded-lg p-3 border border-indigo-700/30">
              <div className="text-indigo-300 text-xs font-medium">VT AMO → R2</div>
              <div className="text-white font-bold text-lg">{data.moyennesEtapes.vt_amo_to_r2 ? data.moyennesEtapes.vt_amo_to_r2 + "j" : "N/A"}</div>
            </div>
            <div className="bg-gradient-to-br from-violet-900/30 to-violet-800/20 rounded-lg p-3 border border-violet-700/30">
              <div className="text-violet-300 text-xs font-medium">VT AMO → Dépôt</div>
              <div className="text-white font-bold text-lg">{data.moyennesEtapes.vt_amo_to_depot ? data.moyennesEtapes.vt_amo_to_depot + "j" : "N/A"}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-lg p-3 border border-purple-700/30">
              <div className="text-purple-300 text-xs font-medium">Dépôt → Octroi</div>
              <div className="text-white font-bold text-lg">{data.moyennesEtapes.depot_to_octroi ? data.moyennesEtapes.depot_to_octroi + "j" : "N/A"}</div>
            </div>
            <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 rounded-lg p-3 border border-amber-700/30">
              <div className="text-amber-300 text-xs font-medium">Octroi → Début</div>
              <div className="text-white font-bold text-lg">{data.moyennesEtapes.octroi_to_debut_chantier ? data.moyennesEtapes.octroi_to_debut_chantier + "j" : "N/A"}</div>
            </div>
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-lg p-3 border border-green-700/30">
              <div className="text-green-300 text-xs font-medium">Début → Fin</div>
              <div className="text-white font-bold text-lg">{data.moyennesEtapes.debut_to_fin_chantier ? data.moyennesEtapes.debut_to_fin_chantier + "j" : "N/A"}</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-400" />
            {filtreStatut ? 'Bloqués à "' + filtreStatut + '"' : "Tous les dossiers bloqués"}
            <span className="text-sm text-slate-400">({filteredDossiers.length})</span>
          </h3>
          {filtreStatut && <button onClick={() => setFiltreStatut("")} className="text-sm text-red-400 hover:text-red-300">Effacer</button>}
        </div>
        
        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3">
          {filteredDossiers.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun dossier bloqué</p>
            </div>
          ) : (
            filteredDossiers.map(function(dossier, idx) {
              return <DossierCard key={dossier.rowNumber || idx} dossier={dossier} />;
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default AnalyseDossiersBloques;
